import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

// The list endpoint returns BriefPublic (camelCase). This was previously typed
// against the legacy Python snake_case shape, so every field came back
// undefined and `createdAt.slice` crashed the whole page.
interface BriefRow {
  id: string
  name: string | null
  status: string
  isLocked: boolean
  readiness: { overall: number | null; grade: string | null } | null
  currentRevisionNumber: number | null
  createdAt: string | null
  updatedAt: string | null
}

type SortKey = 'createdAt' | 'updatedAt'

function gradeClass(score: number | null): string {
  if (score == null) return 'briefer-readiness-pill'
  if (score >= 80) return 'briefer-readiness-pill grade-a'
  if (score >= 60) return 'briefer-readiness-pill grade-b'
  if (score >= 40) return 'briefer-readiness-pill grade-c'
  return 'briefer-readiness-pill grade-d'
}

function statusBadge(status: string, locked: boolean): string {
  if (locked) return 'app-pill app-pill--locked'
  switch (status) {
    case 'submitted':
      return 'app-pill app-pill--ready'
    case 'archived':
      return 'app-pill app-pill--archived'
    default:
      return 'app-pill app-pill--draft'
  }
}

// Map the API status (draft/submitted/archived + isLocked) onto the existing
// uppercase i18n keys (briefs.status.DRAFT/READY/LOCKED/ARCHIVED).
function statusLabelKey(status: string, locked: boolean): string {
  if (locked) return 'LOCKED'
  switch (status) {
    case 'submitted':
      return 'READY'
    case 'archived':
      return 'ARCHIVED'
    default:
      return 'DRAFT'
  }
}

export function Briefs() {
  const { t } = useTranslation()
  const [sort, setSort] = useState<SortKey>('updatedAt')

  const { data: briefs, isLoading } = useQuery({
    queryKey: ['briefs'],
    queryFn: () => api<BriefRow[]>('/api/v1/briefs'),
  })

  const sorted = useMemo(() => {
    if (!briefs) return []
    return [...briefs].sort((a, b) => (b[sort] ?? '').localeCompare(a[sort] ?? ''))
  }, [briefs, sort])

  return (
    <section className="section active" data-section="briefs">
      <div className="section-head">
        <div className="section-num">
          01<span className="dot">.</span>
        </div>
        <div className="section-title-block">
          <div className="label">Workspace · Library</div>
          <h2>{t('briefs.title')}</h2>
          <p className="subtitle">
            {t('briefs.lede', 'Una fila por brief. El estado, la preparación y la última revisión saltan a la vista.')}
          </p>
        </div>
        <div className="section-actions">
          <Link to="/intake/new" className="dock-btn">
            {t('briefs.create')}
          </Link>
        </div>
      </div>

      {isLoading && <p className="briefer-list-empty">{t('common.loading')}</p>}

      {!isLoading && sorted.length === 0 && (
        <p className="briefer-list-empty">{t('briefs.empty')}</p>
      )}

      {sorted.length > 0 && (
        <div className="briefer-list">
          <table className="briefer-table">
            <thead>
              <tr>
                <th>{t('briefs.columns.name')}</th>
                <th>{t('briefs.columns.status')}</th>
                <th>{t('briefs.columns.readiness')}</th>
                <th>{t('briefs.columns.revision')}</th>
                <th>
                  <button
                    type="button"
                    onClick={() => setSort('createdAt')}
                    className={`briefer-table__sort${sort === 'createdAt' ? ' briefer-table__sort--active' : ''}`}
                  >
                    {t('briefs.columns.created')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    onClick={() => setSort('updatedAt')}
                    className={`briefer-table__sort${sort === 'updatedAt' ? ' briefer-table__sort--active' : ''}`}
                  >
                    {t('briefs.columns.updated')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((b) => (
                <tr key={b.id}>
                  <td>
                    <Link to={`/briefs/${b.id}`} className="briefer-table__link">
                      {b.name ?? <span className="briefer-table__muted">—</span>}
                    </Link>
                  </td>
                  <td>
                    <span className={statusBadge(b.status, b.isLocked)}>
                      {t(`briefs.status.${statusLabelKey(b.status, b.isLocked)}`)}
                    </span>
                  </td>
                  <td>
                    <span className={gradeClass(b.readiness?.overall ?? null)}>
                      {b.readiness?.overall != null
                        ? `${b.readiness.overall}${b.readiness.grade ? ` · ${b.readiness.grade}` : ''}`
                        : '—'}
                    </span>
                  </td>
                  <td className="briefer-table__mono">r{b.currentRevisionNumber ?? '—'}</td>
                  <td className="briefer-table__date">{b.createdAt ? b.createdAt.slice(0, 10) : '—'}</td>
                  <td className="briefer-table__date">{b.updatedAt ? b.updatedAt.slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
